import firebase from "../../config/firebase";
import { toast } from "react-toastify";
import { deleteCategory } from "./categoriesActions";

const db = firebase.firestore().collection("menus");

// assume `db` is already something like:
// const db = firebase.firestore().collection("menus");
export const fetchMenus =
  (restaurantID, stopLoader = () => {}) =>
  (dispatch) => {
    const unsubscribe = db
      .where("restaurantID", "==", restaurantID)
      .orderBy("position", "asc")
      .onSnapshot(
        async (querySnapshot) => {
          try {
            const enrichedMenus = await Promise.all(
              querySnapshot.docs.map(async (doc) => {
                const menus = { id: doc.id, ...doc.data() };

                const itemSnap = await firebase
                  .firestore()
                  .collection("items")
                  .where("menuID", "==", menus.id)
                  .limit(1)
                  .get();

                return { ...menus, hasItems: !itemSnap.empty };
              })
            );

            dispatch({
              type: "FETCH_MENUS_SUCCESS",
              payload: enrichedMenus,
            });
          } catch (err) {
            console.error("Error enriching menus:", err);
            toast.error(err.message || "Unable to fetch menus");
          } finally {
            stopLoader();
          }
        },
        (error) => {
          console.error("Error occurred: ", error.message);
          toast.error(error.message || "Unable to fetch menus");
          stopLoader();
        }
      );

    return unsubscribe;
  };

export const addMenu = (restaurantID, name) => async (dispatch) => {
  try {
    const menusSnapshot = await db
      .where("restaurantID", "==", restaurantID)
      .orderBy("position", "desc")
      .limit(1)
      .get();

    let lastPosition = 0;
    if (!menusSnapshot.empty) {
      const lastMenu = menusSnapshot.docs[0].data();
      lastPosition = lastMenu.position;
    }

    await db.add({
      name,
      restaurantID,
      position: lastPosition + 1,
      isVisible: true,
      createdAt: firebase.firestore.Timestamp.now(),
      updatedAt: firebase.firestore.Timestamp.now(),
    });

    toast.success("Menu Added Successfully");
  } catch (error) {
    console.error(error.message);
    toast.error(error.message || "Unable to add Menu");
  }
};

export const updateMenu = (id, updatedName) => async () => {
  try {
    await db.doc(id).update({
      name: updatedName,
      updatedAt: firebase.firestore.Timestamp.now(),
    });
    toast.success("Menu Updated Successfully");
  } catch (error) {
    console.error(error.message);
    toast.error(error.message || "Unable to update Menu");
  }
};

export const deleteMenu = (menuID) => async (dispatch) => {
  return new Promise(async (resolve, reject) => {
    try {
      const categoriesSnapshot = await firebase
        .firestore()
        .collection("categories")
        .where("menuID", "==", menuID)
        .get();

      const categoriesData = categoriesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      for (const category of categoriesData) {
        try {
          await dispatch(deleteCategory(category.id, category?.imageUrl));
        } catch (err) {
          console.error("Error while deleting category", err);
        }
      }

      await db.doc(menuID).delete();

      toast.success("Menu Deleted Successfully");

      dispatch({
        type: "DELETE_MENU_SUCCESS",
        payload: menuID,
      });

      resolve(menuID);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message || "Unable to delete Menu");
      reject(error);
    }
  });
};

export const toggleMenuVisibility =
  (id, isVisible, onSuccess = () => {}) =>
  async () => {
    try {
      await db.doc(id).update({ isVisible });
      onSuccess();
      toast.success("Menu visibility updated successfully!");
    } catch (error) {
      console.error("Error updating menu visibility:", error);
      toast.error("Failed to update menu visibility.");
    }
  };

export const reorderParentMenus = (parentMenu) => async () => {
  try {
    const updateBatch = firebase.firestore().batch();
    parentMenu.forEach((item) => {
      const docRef = db.doc(item.id);
      updateBatch.update(docRef, {
        ...item,
      });
    });
    await updateBatch.commit();
  } catch (error) {
    toast.error(error.message);
  }
};

export const fetchMenuCategoriesAndItems =
  (id, onSuccess = () => {}) =>
  async () => {
    try {
      const categoriesQuery = await firebase
        .firestore()
        .collection("categories")
        .where("menuID", "==", id)
        .get();

      const categoriesPromises = categoriesQuery.docs.map(async (doc) => {
        if (doc.exists) {
          const itemsQuery = await firebase
            .firestore()
            .collection("items")
            .where("categoryId", "==", doc.id)
            .get();

          const items = itemsQuery.docs
            .map((item) =>
              item.exists ? { id: item.id, ...item.data() } : null
            )
            .filter(Boolean);

          return { id: doc.id, ...doc.data(), items };
        }
      });

      const categories = await Promise.all(categoriesPromises);

      onSuccess(categories.filter(Boolean));
    } catch (error) {
      console.error("Error fetching categories and items:", error);
    }
  };
